import { DateTime } from 'luxon';
import type { JSX } from 'react';
import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import * as FilteredPermitsState from '../../state/FilteredPermitsState.ts';
import { DatePicker } from '../../../ui/date-picker/DatePicker.tsx';
import * as FilteredPermitsActions from '../../actions/FilteredPermitsActions.ts';
import { SingleSelection } from '../../../ui/labeled-elements/SingleSelection.tsx';
import * as Text from '../../../common/i18n/Text.ts';
import { issueDateFilterTexts } from '../../i18n/FilteredPermitsTexts.ts';
import {
    HalfSlideIn,
    SecondLevelHeader,
    SlideInBody,
    SlideInHeaderTexts,
} from '../../../ui/slidein/Slidein.tsx';
import { DateHeader } from '../shared/DateHeader.tsx';
import { css } from '@emotion/css';

export interface IssueDateFilterTexts {
    filterTitle: Text.Translation;
    from: Text.Translation;
    to: Text.Translation;
}

export function IssueDateFilterHalfSlideIn() {
    const { storeState, update } = useStore(store => ({
        filter: FilteredPermitsState.Filter.get(store),
    }));
    const { language } = useLanguage();

    function getTexts(): IssueDateFilterTexts {
        return issueDateFilterTexts[language];
    }

    function renderFromDatePicker(): JSX.Element {
        const texts = getTexts();
        return (
            <div className={css({ marginBottom: '30px' })}>
                <DateHeader
                    label={texts.from()}
                    date={storeState.filter.issueDateFrom}
                />
                <DatePicker
                    currentDate={DateTime.now()}
                    selectedDate={storeState.filter.issueDateFrom}
                    onSelect={d =>
                        update(store =>
                            FilteredPermitsActions.setIssueDateFrom(store, d),
                        )
                    }
                    language={language}
                />
            </div>
        );
    }

    function renderToDatePicker(): JSX.Element {
        const texts = getTexts();
        return (
            <>
                <DateHeader
                    label={texts.to()}
                    date={storeState.filter.issueDateTo}
                />
                <DatePicker
                    currentDate={DateTime.now()}
                    selectedDate={storeState.filter.issueDateTo}
                    onSelect={d =>
                        update(store =>
                            FilteredPermitsActions.setIssueDateTo(store, d),
                        )
                    }
                    language={language}
                />
            </>
        );
    }

    const texts = getTexts();
    return (
        <HalfSlideIn
            open={storeState.filter.issueDateSelectionVisible || false}
        >
            <SecondLevelHeader
                onClose={() =>
                    update(store =>
                        FilteredPermitsState.Filter.stateWrite(store, {
                            issueDateSelectionVisible: false,
                        }),
                    )
                }
            >
                <SlideInHeaderTexts
                    subtitle={''}
                    title={texts.filterTitle()}
                    hasLeftIcon={false}
                />
            </SecondLevelHeader>
            <SlideInBody>
                <div
                    className={css({
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0,
                        overflowX: 'hidden',
                        overflowY: 'auto',
                    })}
                >
                    <div
                        className={css({
                            width: '280px',
                            margin: '30px auto 0 auto',
                        })}
                    >
                        {renderFromDatePicker()}
                        {renderToDatePicker()}
                    </div>
                </div>
            </SlideInBody>
        </HalfSlideIn>
    );
}

export function IssueDateFilterSelection() {
    const { storeState, update } = useStore(store => ({
        filter: FilteredPermitsState.Filter.get(store),
    }));
    const { language } = useLanguage();

    const texts = issueDateFilterTexts[language];
    const clear = FilteredPermitsState.Filter.issueDateFilterActive(
        storeState.filter,
    )
        ? () => update(FilteredPermitsActions.clearIssueDate)
        : null;
    const selection = Formatter.openRange(
        storeState.filter.issueDateFrom,
        storeState.filter.issueDateTo,
        Formatter.dayMonthYear,
    );

    return (
        <SingleSelection
            label={texts.filterTitle()}
            focused={false}
            selection={selection}
            onClick={() =>
                update(store =>
                    FilteredPermitsState.Filter.stateWrite(store, {
                        issueDateSelectionVisible: true,
                    }),
                )
            }
            onClear={clear}
        />
    );
}
